import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import Cropper from 'react-easy-crop';
import { getTutorProfile, updateTutorProfile, getPublicSetting } from '../api/supabaseAPI';
import Loader from '../components/Loader';
import ConfirmDialog from '../components/ConfirmDialog';
import WorkHistoryEditor from '../components/WorkHistoryEditor';
import ToastStack from '../components/ToastStack';
import '../styles/Profile.css';

// =================================================================
// ==   Helpers                                                    ==
// =================================================================

const shallowEqual = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  const keysA = Object.keys(a);
  if (keysA.length !== Object.keys(b).length) return false;
  return keysA.every((k) => a[k] === b[k]);
};

const R2_PUBLIC_BASE_URL_ENV = (import.meta.env.VITE_R2_PUBLIC_BASE_URL || '').replace(/\/$/, '');

const createImage = (url) => new Promise((resolve, reject) => {
  const image = new Image();
  image.addEventListener('load', () => resolve(image));
  image.addEventListener('error', (error) => reject(error));
  image.src = url;
});

const getOutputType = () => 'image/png';

const getExtensionFromType = () => 'png';

const parseBackgroundYIndex = (path) => {
  if (!path) return 0;
  const match = path.match(/background_(\d+)(?:_v\d+)?\.png(?:\?.*)?$/i);
  if (!match) return 0;
  return Number(match[1]) || 0;
};

const getCroppedImageBlob = async (imageSrc, cropPixels, inputType, maxSize = 1080) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context not available.');
  }

  const maxDimension = Math.max(cropPixels.width, cropPixels.height);
  const scale = maxDimension > maxSize ? maxSize / maxDimension : 1;
  const targetWidth = Math.round(cropPixels.width * scale);
  const targetHeight = Math.round(cropPixels.height * scale);

  canvas.width = targetWidth;
  canvas.height = targetHeight;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    targetWidth,
    targetHeight
  );

  const outputType = getOutputType(inputType);
  const blob = await new Promise((resolve) => {
    canvas.toBlob((result) => resolve(result), outputType);
  });
  if (!blob) {
    throw new Error('Failed to export cropped image.');
  }

  return {
    blob,
    outputType,
    extension: getExtensionFromType(outputType),
  };
};

const COVER_Y_INDEX_MAX = 1000;
const COVER_PREVIEW_HEIGHT = 180;
const COVER_PREVIEW_MIN_WIDTH = 360;
const COVER_PREVIEW_MAX_WIDTH = 1080;
const COVER_PREVIEW_DEFAULT_WIDTH = 720;

// =================================================================
// ==   Custom Hook: Update Tutor Profile                          ==
// =================================================================

const useUpdateTutorProfile = (language, userId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates) => {
      // Work history is stored as JSONB
      // If it's an array (from new editor), use it directly.
      // If it's a string (legacy behavior fallback), split it.
      const { work_history, ...otherUpdates } = updates;
      
      let finalWorkHistory = [];
      if (Array.isArray(work_history)) {
        // Filter out empty/invalid entries and clean up the data
        finalWorkHistory = work_history
          .filter(entry => {
            // Only keep entries that have at least role and startDate
            const hasRole = entry.role && entry.role.trim() !== '';
            const hasStartDate = entry.startDate && entry.startDate !== '';
            return hasRole && hasStartDate;
          })
          .map(entry => ({
            id: entry.id,
            role: entry.role?.trim() || '',
            company: entry.company?.trim() || '',
            startDate: entry.startDate || '',
            endDate: entry.isCurrent ? '' : (entry.endDate || ''),
            isCurrent: entry.isCurrent || false,
            description: entry.description?.trim() || ''
          }));
      } else if (typeof work_history === 'string') {
        finalWorkHistory = work_history.split('\n').filter(line => line.trim() !== '');
      }
      
      await updateTutorProfile(userId, language, {
        ...otherUpdates,
        ...(finalWorkHistory.length > 0 && { work_history: finalWorkHistory }),
        ...(finalWorkHistory.length === 0 && { work_history: [] }), // Allow clearing
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tutorProfile', userId, language] });
    },
    onError: (error) => {
      console.error("Update failed:", error);
    },
  });
};

// =================================================================
// ==   TutorProfile Component                                     ==
// =================================================================

export default function TutorProfile() {
  const { t } = useTranslation();
  const session = useSession();
  const supabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();
  const { language } = useParams();
  const userId = session?.user?.id;

  const { mutate: updateProfile, isPending: isUpdating, isSuccess, isError, error, reset } = useUpdateTutorProfile(language, userId);

  // Form Data State
  const [formData, setFormData] = useState({
    full_name: '',
    display_name: '',
    headline: '',
    bio: '',
    years_of_experience: 0,
    work_history: [],
  });

  // Avatar State
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);
  const [avatarSrc, setAvatarSrc] = useState('');
  const [avatarFileType, setAvatarFileType] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [avatarMessage, setAvatarMessage] = useState({ text: '', type: '' });
  const [isAvatarUploading, setIsAvatarUploading] = useState(false);
  const [avatarCacheBust, setAvatarCacheBust] = useState(() => localStorage.getItem('avatarCacheBust') || '');
  const avatarObjectUrlRef = useRef(null);

  // Work History Validation State
  const [isWorkHistoryValid, setIsWorkHistoryValid] = useState(true);
  const avatarInputRef = useRef(null);
  const avatarImgRef = useRef(null);

  // Cover State
  const [isCoverModalOpen, setIsCoverModalOpen] = useState(false);
  const [coverDraftSrc, setCoverDraftSrc] = useState('');
  const [coverActiveSrc, setCoverActiveSrc] = useState('');
  const [coverDraftYIndex, setCoverDraftYIndex] = useState(0);
  const [coverActiveYIndex, setCoverActiveYIndex] = useState(0);
  const [coverPreviewWidth, setCoverPreviewWidth] = useState(COVER_PREVIEW_DEFAULT_WIDTH);
  const [coverDraftSize, setCoverDraftSize] = useState({ width: 0, height: 0 });
  const [coverPreviewMaxOffset, setCoverPreviewMaxOffset] = useState(0);
  const [coverDisplaySize, setCoverDisplaySize] = useState({ width: 0, height: 0 });
  const [coverDragMode, setCoverDragMode] = useState(null);
  const [coverMessage, setCoverMessage] = useState({ text: '', type: '' });
  const [isCoverUploading, setIsCoverUploading] = useState(false);
  const [coverActiveReadyTick, setCoverActiveReadyTick] = useState(0);
  const coverDraftFileRef = useRef(null);
  const coverDraftObjectUrlRef = useRef(null);
  const coverActiveObjectUrlRef = useRef(null);
  const coverDraftImageRef = useRef(null);
  const coverActiveImageRef = useRef(null);
  const coverActiveSrcRef = useRef('');
  const coverPreviewCanvasRef = useRef(null);
  const coverDisplayCanvasRef = useRef(null);
  const coverDisplayRef = useRef(null);
  const coverIgnoreCloseRef = useRef(false);
  const coverDragStartRef = useRef({ x: 0, y: 0, width: COVER_PREVIEW_DEFAULT_WIDTH, yIndex: 0 });
  const coverInputRef = useRef(null);

  // Utility Refs
  const isUpdatingRef = useRef(false);
  const lastSyncedFormRef = useRef(formData);
  const isDirtyRef = useRef(false);

  // Fetch Tutor Profile
  const { data: profileData, isLoading } = useQuery({
    queryKey: ['tutorProfile', userId, language],
    queryFn: () => getTutorProfile(userId, language),
    enabled: !!userId && !!language,
    staleTime: 1000 * 60 * 5,
  });

  // Fetch R2 Public Base URL
  const { data: r2PublicBaseUrl } = useQuery({
    queryKey: ['public_settings', 'r2_public_base_url'],
    queryFn: () => getPublicSetting('r2_public_base_url'),
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });

  // Computed Values
  const publicBaseUrl = useMemo(() => {
    const rawBaseUrl = R2_PUBLIC_BASE_URL_ENV || r2PublicBaseUrl || '';
    return rawBaseUrl ? rawBaseUrl.replace(/\/$/, '') : '';
  }, [r2PublicBaseUrl]);

  const isExternalUrl = useCallback((value) => /^https?:\/\//i.test(value || ''), []);

  const avatarUrl = useMemo(() => {
    const avatarPath = profileData?.avatar;
    if (avatarPath) {
      if (isExternalUrl(avatarPath)) return avatarPath;
      if (!publicBaseUrl) return avatarPath;
      const baseUrl = `${publicBaseUrl}/${avatarPath}`;
      return avatarCacheBust ? `${baseUrl}?v=${avatarCacheBust}` : baseUrl;
    }
    return '/Resources/imagine/default_avatar/default_avatar.jpeg';
  }, [avatarCacheBust, publicBaseUrl, profileData?.avatar, isExternalUrl]);

  const [isAvatarLoaded, setIsAvatarLoaded] = useState(false);
  const [isAvatarError, setIsAvatarError] = useState(false);
  const showAvatarSkeleton = isLoading || (!isAvatarLoaded && !isAvatarError);

  // Helper: getCoverMaxOffset
  const getCoverMaxOffset = useCallback((imageSize, targetWidth, targetHeight) => {
    if (!imageSize?.width || !imageSize?.height || !targetWidth || !targetHeight) return 0;
    const scale = targetWidth / imageSize.width;
    const scaledHeight = imageSize.height * scale;
    return Math.max(0, Math.round(scaledHeight - targetHeight));
  }, []);

  // Helper: drawCoverCanvas
  const drawCoverCanvas = useCallback((canvas, image, targetWidth, targetHeight, yIndex) => {
    if (!canvas || !image || !targetWidth || !targetHeight) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const scale = targetWidth / image.width;
    const scaledHeight = image.height * scale;
    const maxOffset = Math.max(0, Math.round(scaledHeight - targetHeight));
    const clampedIndex = Math.min(COVER_Y_INDEX_MAX, Math.max(0, yIndex));
    const offset = Math.round((clampedIndex / COVER_Y_INDEX_MAX) * maxOffset);

    ctx.clearRect(0, 0, targetWidth, targetHeight);
    ctx.drawImage(image, 0, -offset, targetWidth, scaledHeight);
  }, []);

  // Helper: clampCoverPreviewWidth
  const clampCoverPreviewWidth = useCallback((nextWidth) => {
    return Math.min(COVER_PREVIEW_MAX_WIDTH, Math.max(COVER_PREVIEW_MIN_WIDTH, Math.round(nextWidth)));
  }, []);

  // Sync form data from profileData
  useEffect(() => {
    if (!profileData) return;

    const nextForm = {
      full_name: profileData?.full_name || '',
      display_name: profileData?.display_name || '',
      headline: profileData?.headline || '',
      bio: profileData?.bio || '',
      years_of_experience: profileData?.years_of_experience || 0,
      work_history: profileData?.work_history || [],
    };

    const lastSynced = lastSyncedFormRef.current;
    const backendChanged = !shallowEqual(nextForm, lastSynced);
    const userUnchangedLocally = shallowEqual(formData, lastSynced);

    if (backendChanged && userUnchangedLocally) {
      setFormData(nextForm);
      lastSyncedFormRef.current = nextForm;
      isDirtyRef.current = false;
    }
  }, [profileData, formData]);

  // Cleanup Object URLs
  useEffect(() => {
    return () => {
      if (avatarObjectUrlRef.current) {
        URL.revokeObjectURL(avatarObjectUrlRef.current);
        avatarObjectUrlRef.current = null;
      }
      if (coverDraftObjectUrlRef.current) {
        URL.revokeObjectURL(coverDraftObjectUrlRef.current);
        coverDraftObjectUrlRef.current = null;
      }
      if (coverActiveObjectUrlRef.current) {
        URL.revokeObjectURL(coverActiveObjectUrlRef.current);
        coverActiveObjectUrlRef.current = null;
      }
    };
  }, []);

  // Auto-dismiss toast messages
  useEffect(() => {
    if (isSuccess || isError) {
      isUpdatingRef.current = false;
      isDirtyRef.current = false;
      const timer = setTimeout(() => {
        reset();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, isError, reset]);

  // Sync cache bust
  useEffect(() => {
    const syncCacheBust = (event) => {
      if (event?.type === 'storage' && event.key !== 'avatarCacheBust') return;
      const nextValue = localStorage.getItem('avatarCacheBust') || '';
      setAvatarCacheBust(nextValue);
    };
    window.addEventListener('storage', syncCacheBust);
    window.addEventListener('avatar-cache-bust', syncCacheBust);
    return () => {
      window.removeEventListener('storage', syncCacheBust);
      window.removeEventListener('avatar-cache-bust', syncCacheBust);
    };
  }, []);

  // Reset avatar load state when URL changes
  useEffect(() => {
    if (!avatarUrl) {
      setIsAvatarLoaded(false);
      setIsAvatarError(false);
      return;
    }
    setIsAvatarLoaded(false);
    setIsAvatarError(false);
  }, [avatarUrl]);

  // Check if avatar image is loaded
  useEffect(() => {
    if (!avatarUrl || !avatarImgRef.current) return;
    if (avatarImgRef.current.complete && avatarImgRef.current.naturalWidth > 0) {
      setIsAvatarLoaded(true);
    }
  }, [avatarUrl]);

  // Auto-dismiss avatar message
  useEffect(() => {
    if (avatarMessage.text) {
      const timer = setTimeout(() => {
        setAvatarMessage({ text: '', type: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [avatarMessage]);

  // Auto-dismiss cover message
  useEffect(() => {
    if (coverMessage.text) {
      const timer = setTimeout(() => {
        setCoverMessage({ text: '', type: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [coverMessage]);

  // Load cover draft image
  useEffect(() => {
    let isMounted = true;
    if (!coverDraftSrc) {
      coverDraftImageRef.current = null;
      setCoverDraftSize({ width: 0, height: 0 });
      return () => {};
    }

    createImage(coverDraftSrc)
      .then((image) => {
        if (!isMounted) return;
        coverDraftImageRef.current = image;
        setCoverDraftSize({ width: image.naturalWidth || image.width, height: image.naturalHeight || image.height });
      })
      .catch(() => {
        if (!isMounted) return;
        coverDraftImageRef.current = null;
        setCoverDraftSize({ width: 0, height: 0 });
      });

    return () => {
      isMounted = false;
    };
  }, [coverDraftSrc]);

  // Load cover active image
  useEffect(() => {
    if (!coverActiveSrc) {
      coverActiveImageRef.current = null;
      coverActiveSrcRef.current = '';
      return;
    }
    if (coverActiveSrcRef.current === coverActiveSrc && coverActiveImageRef.current) return;

    let isMounted = true;
    createImage(coverActiveSrc)
      .then((image) => {
        if (!isMounted) return;
        coverActiveImageRef.current = image;
        coverActiveSrcRef.current = coverActiveSrc;
        setCoverActiveReadyTick((value) => value + 1);
      })
      .catch(() => {
        if (!isMounted) return;
        coverActiveImageRef.current = null;
        coverActiveSrcRef.current = '';
      });

    return () => {
      isMounted = false;
    };
  }, [coverActiveSrc]);

  // Update active cover from profileData
  useEffect(() => {
    const backgroundPath = profileData?.background || '';
    if (!backgroundPath) {
      setCoverActiveSrc('');
      setCoverActiveYIndex(0);
      return;
    }
    const baseUrl = publicBaseUrl ? `${publicBaseUrl}/${backgroundPath}` : backgroundPath;
    setCoverActiveSrc(baseUrl);
    setCoverActiveYIndex(parseBackgroundYIndex(backgroundPath));
  }, [publicBaseUrl, profileData?.background]);

  // Update preview max offset
  useEffect(() => {
    const maxOffset = getCoverMaxOffset(coverDraftSize, coverPreviewWidth, COVER_PREVIEW_HEIGHT);
    setCoverPreviewMaxOffset(maxOffset);
  }, [coverDraftSize, coverPreviewWidth, getCoverMaxOffset]);

  // Handle cover drag/resize
  useEffect(() => {
    if (!coverDragMode) return;

    const handlePointerMove = (event) => {
      if (coverDragMode === 'pan') {
        if (!coverPreviewMaxOffset) return;
        const deltaY = coverDragStartRef.current.y - event.clientY;
        const ratio = COVER_Y_INDEX_MAX / coverPreviewMaxOffset;
        const nextYIndex = coverDragStartRef.current.yIndex + deltaY * ratio;
        setCoverDraftYIndex(Math.min(COVER_Y_INDEX_MAX, Math.max(0, Math.round(nextYIndex))));
        return;
      }

      const deltaX = event.clientX - coverDragStartRef.current.x;
      const direction = coverDragMode === 'resize-left' ? -1 : 1;
      const nextWidth = coverDragStartRef.current.width + deltaX * direction;
      setCoverPreviewWidth(clampCoverPreviewWidth(nextWidth));
    };

    const handlePointerUp = () => {
      setCoverDragMode(null);
      setTimeout(() => {
        coverIgnoreCloseRef.current = false;
      }, 0);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [coverDragMode, coverPreviewMaxOffset, clampCoverPreviewWidth]);

  // Draw cover preview canvas
  useEffect(() => {
    if (!coverPreviewCanvasRef.current || !coverDraftImageRef.current) return;
    drawCoverCanvas(
      coverPreviewCanvasRef.current,
      coverDraftImageRef.current,
      coverPreviewWidth,
      COVER_PREVIEW_HEIGHT,
      coverDraftYIndex
    );
  }, [coverDraftYIndex, coverPreviewWidth, coverDraftSrc, coverDraftSize, drawCoverCanvas]);

  // Observe cover display size
  useEffect(() => {
    if (!coverDisplayRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setCoverDisplaySize({ width: Math.round(width), height: Math.round(height) });
    });
    observer.observe(coverDisplayRef.current);
    return () => observer.disconnect();
  }, [isLoading]);

  // Draw cover display canvas
  useEffect(() => {
    if (!coverDisplayCanvasRef.current || !coverActiveImageRef.current) return;
    if (!coverDisplaySize.width || !coverDisplaySize.height) return;
    drawCoverCanvas(
      coverDisplayCanvasRef.current,
      coverActiveImageRef.current,
      coverDisplaySize.width,
      coverDisplaySize.height,
      coverActiveYIndex
    );
  }, [coverActiveYIndex, coverActiveSrc, coverDisplaySize, coverActiveReadyTick, drawCoverCanvas]);

  // Event Handlers
  const startCoverResize = useCallback((side, event) => {
    event.preventDefault();
    event.stopPropagation();
    coverIgnoreCloseRef.current = true;
    coverDragStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      width: coverPreviewWidth,
      yIndex: coverDraftYIndex,
    };
    setCoverDragMode(side);
  }, [coverPreviewWidth, coverDraftYIndex]);

  const startCoverPan = useCallback((event) => {
    if (!coverDraftSrc || !coverDraftSize.width) return;
    event.preventDefault();
    coverIgnoreCloseRef.current = true;
    coverDragStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      width: coverPreviewWidth,
      yIndex: coverDraftYIndex,
    };
    setCoverDragMode('pan');
  }, [coverDraftSrc, coverDraftSize.width, coverPreviewWidth, coverDraftYIndex]);

  const closeAvatarModal = useCallback(() => {
    setIsAvatarModalOpen(false);
    setAvatarSrc('');
    setAvatarFileType('');
    setCroppedAreaPixels(null);
    setIsAvatarUploading(false);
    if (avatarObjectUrlRef.current) {
      URL.revokeObjectURL(avatarObjectUrlRef.current);
      avatarObjectUrlRef.current = null;
    }
    if (avatarInputRef.current) {
      avatarInputRef.current.value = '';
    }
  }, []);

  const closeCoverModal = useCallback(() => {
    setIsCoverModalOpen(false);
    setCoverDraftSrc('');
    setCoverDraftYIndex(0);
    setCoverPreviewWidth(COVER_PREVIEW_DEFAULT_WIDTH);
    setCoverDraftSize({ width: 0, height: 0 });
    setCoverPreviewMaxOffset(0);
    setCoverDragMode(null);
    setIsCoverUploading(false);
    coverDraftImageRef.current = null;
    coverDraftFileRef.current = null;
    if (coverDraftObjectUrlRef.current) {
      URL.revokeObjectURL(coverDraftObjectUrlRef.current);
      coverDraftObjectUrlRef.current = null;
    }
    if (coverInputRef.current) {
      coverInputRef.current.value = '';
    }
  }, []);

  const handleCoverOverlayClick = useCallback(() => {
    if (coverIgnoreCloseRef.current || isCoverUploading) return;
    closeCoverModal();
  }, [closeCoverModal, isCoverUploading]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    isDirtyRef.current = true;
    setFormData(prevData => ({ ...prevData, [name]: value }));
  };

  const handleAvatarFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (avatarObjectUrlRef.current) {
      URL.revokeObjectURL(avatarObjectUrlRef.current);
    }

    const objectUrl = URL.createObjectURL(file);
    avatarObjectUrlRef.current = objectUrl;
    setAvatarSrc(objectUrl);
    setAvatarFileType(file.type);
    setIsAvatarModalOpen(true);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const handleCoverFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (coverDraftObjectUrlRef.current) {
      URL.revokeObjectURL(coverDraftObjectUrlRef.current);
    }

    const objectUrl = URL.createObjectURL(file);
    coverDraftObjectUrlRef.current = objectUrl;
    coverDraftFileRef.current = file;
    setCoverDraftSrc(objectUrl);
    setCoverDraftYIndex(0);
    setCoverPreviewWidth(COVER_PREVIEW_DEFAULT_WIDTH);
    setCoverDragMode(null);
    setIsCoverModalOpen(true);
  };

  const handleCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleAvatarSave = async () => {
    if (isAvatarUploading) return;
    if (!avatarSrc || !croppedAreaPixels) {
      closeAvatarModal();
      return;
    }

    try {
      setIsAvatarUploading(true);
      const croppedResult = await getCroppedImageBlob(avatarSrc, croppedAreaPixels, avatarFileType);
      if (!croppedResult?.blob) {
        throw new Error('Failed to prepare image.');
      }

      const fileName = 'avatar.png';
      const croppedFile = new File([croppedResult.blob], fileName, { type: croppedResult.outputType });
      const formData = new FormData();
      formData.append('file', croppedFile);
      formData.append('kind', 'avatar');
      formData.append('language', language); // ✨ Add language for tutor profile

      const { data, error: uploadError } = await supabaseClient.functions.invoke('handle-profile-images', {
        body: formData,
      });

      if (uploadError) {
        throw new Error(uploadError.message || 'Upload failed.');
      }

      if (!data?.path) {
        throw new Error('Upload succeeded but no path returned.');
      }

      const cacheBust = String(Date.now());
      setAvatarCacheBust(cacheBust);
      localStorage.setItem('avatarCacheBust', cacheBust);
      window.dispatchEvent(new Event('avatar-cache-bust'));
      setAvatarMessage({ text: 'Avatar updated successfully!', type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['tutorProfile', userId, language] });
      closeAvatarModal();
    } catch (cropError) {
      console.error('Avatar crop failed:', cropError);
      setAvatarMessage({ text: cropError.message || 'Failed to update avatar.', type: 'error' });
    } finally {
      setIsAvatarUploading(false);
    }
  };

  const handleCoverSave = async () => {
    if (isCoverUploading) return;
    if (!coverDraftSrc || !coverDraftImageRef.current || !coverDraftFileRef.current) {
      closeCoverModal();
      return;
    }

    try {
      setIsCoverUploading(true);
      const formData = new FormData();
      formData.append('file', coverDraftFileRef.current);
      formData.append('kind', 'background');
      formData.append('yIndex', String(coverDraftYIndex));
      formData.append('language', language); // ✨ Add language for tutor profile

      const { data, error: uploadError } = await supabaseClient.functions.invoke('handle-profile-images', {
        body: formData,
      });

      if (uploadError) {
        throw new Error(uploadError.message || 'Upload failed.');
      }

      if (!data?.path) {
        throw new Error('Upload succeeded but no path returned.');
      }

      const nextBackgroundUrl = publicBaseUrl ? `${publicBaseUrl}/${data.path}` : data.path;
      if (coverActiveObjectUrlRef.current) {
        URL.revokeObjectURL(coverActiveObjectUrlRef.current);
        coverActiveObjectUrlRef.current = null;
      }

      if (coverDraftObjectUrlRef.current) {
        URL.revokeObjectURL(coverDraftObjectUrlRef.current);
        coverDraftObjectUrlRef.current = null;
      }
      coverActiveImageRef.current = coverDraftImageRef.current;
      coverActiveSrcRef.current = '';
      setCoverActiveSrc(nextBackgroundUrl);
      setCoverActiveYIndex(coverDraftYIndex);
      setCoverMessage({ text: 'Cover updated successfully!', type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['tutorProfile', userId, language] });
      setCoverDraftSrc('');
      closeCoverModal();
    } catch (coverError) {
      console.error('Cover upload failed:', coverError);
      setCoverMessage({ text: coverError.message || 'Failed to update cover.', type: 'error' });
    } finally {
      setIsCoverUploading(false);
    }
  };

  const handleRemoveAvatar = () => {
    if (isAvatarUploading) return;
    setIsRemoveConfirmOpen(true);
  };

  const executeRemoveAvatar = async () => {
    try {
      setIsRemoveConfirmOpen(false);
      setIsAvatarUploading(true);
      const { error: removeError } = await supabaseClient.functions.invoke('handle-profile-images', {
        method: 'POST',
        body: { action: 'delete', kind: 'avatar', language: language }, // ✨ Add language
      });

      if (removeError) {
        throw new Error(removeError.message || 'Remove failed.');
      }

      const cacheBust = String(Date.now());
      setAvatarCacheBust(cacheBust);
      localStorage.setItem('avatarCacheBust', cacheBust);
      window.dispatchEvent(new Event('avatar-cache-bust'));
      
      setAvatarMessage({ text: 'Avatar removed successfully!', type: 'success' });
      queryClient.invalidateQueries({ queryKey: ['tutorProfile', userId, language] });
    } catch (err) {
      console.error('Avatar remove failed:', err);
      setAvatarMessage({ text: err.message || 'Failed to remove avatar.', type: 'error' });
    } finally {
      setIsAvatarUploading(false);
    }
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    if (!isWorkHistoryValid) {
      return; // Don't submit if work history has validation errors
    }
    isUpdatingRef.current = true;
    updateProfile(formData);
  };

  const message = useMemo(() => {
    if (isSuccess) {
      return { text: 'Profile updated successfully!', type: 'success' };
    }
    if (isError) {
      if (error.message === "No changes detected.") {
        return { text: error.message, type: 'info' };
      }
      return { text: `Update failed: ${error.message}`, type: 'error' };
    }
    return { text: '', type: '' };
  }, [isSuccess, isError, error]);

  if (isLoading) return <main className="profile-page-container"><h1 className="page-title">{t('profilePage.title')}</h1><Loader /></main>;

  const hasCoverDraft = Boolean(coverDraftSrc && coverDraftSize.width);
  const isCoverPanning = coverDragMode === 'pan';
  const coverYIndexRatio = COVER_Y_INDEX_MAX === 0 ? 0 : Math.min(1, Math.max(0, coverDraftYIndex / COVER_Y_INDEX_MAX));

  return (
    <main className="profile-page-container">
      <ToastStack items={[message, avatarMessage, coverMessage]} />

      <h1 className="page-title">{t('profilePage.title')} - {language}</h1>

      <section className="profile-shell" aria-label="Tutor profile shell">
        <div className="profile-hero">
          <div
            className="profile-cover"
            ref={coverDisplayRef}
            role="img"
            aria-label="Tutor profile cover"
          >
            <canvas
              className="profile-cover-canvas"
              ref={coverDisplayCanvasRef}
              width={coverDisplaySize.width || 1}
              height={coverDisplaySize.height || 1}
              aria-hidden="true"
            />
            <div className="profile-cover-actions">
              <input
                id="tutor-cover-upload"
                className="profile-file-input"
                ref={coverInputRef}
                type="file"
                accept="image/*"
                onChange={handleCoverFileChange}
              />
              <label className="profile-ghost-btn" htmlFor="tutor-cover-upload">
                {t('profilePage.changeCover', 'Change cover')}
              </label>
            </div>
          </div>

          <div className="profile-hero-content">
            <div className="profile-avatar-block">
              <div className="profile-avatar" aria-hidden="true">
                {showAvatarSkeleton && (
                  <div className={`profile-avatar-skeleton ${isAvatarLoaded ? 'is-hidden' : ''}`} />
                )}
                <img
                  className={`profile-avatar-img ${isAvatarLoaded ? 'is-visible' : ''}`}
                  src={avatarUrl}
                  alt="Tutor avatar"
                  ref={avatarImgRef}
                  key={avatarUrl}
                  onLoad={() => setIsAvatarLoaded(true)}
                  onError={(e) => {
                    setIsAvatarError(true);
                    e.target.src = '/Resources/imagine/default_avatar/default_avatar.jpeg';
                  }}
                />
              </div>

              <div className="profile-avatar-actions">
                <input
                  id="tutor-avatar-upload"
                  className="profile-file-input"
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarFileChange}
                />
                <label className="profile-ghost-btn" htmlFor="tutor-avatar-upload">
                  {t('profilePage.changePhoto', 'Upload photo')}
                </label>
                <button 
                  type="button" 
                  className="profile-text-btn" 
                  onClick={handleRemoveAvatar}
                  disabled={!profileData?.avatar || isAvatarUploading}
                >
                  {t('profilePage.removePhoto', 'Remove')}
                </button>
              </div>
            </div>

            <div className="profile-identity">
              <div className="profile-name-row">
                <div className="profile-name">{formData.display_name || formData.full_name || t('profilePage.noName', 'Tutor')}</div>
                <span className="profile-badge">Tutor</span>
              </div>
              <div className="profile-handle">{formData.headline}</div>
            </div>
          </div>
        </div>

        <form id="tutor-profile-form" className="profile-form" onSubmit={handleUpdate}>
          <div className="profile-panel-body">
            <div className="input-group">
              <label htmlFor="full-name">Full Name</label>
              <input
                type="text"
                id="full-name"
                name="full_name"
                value={formData.full_name}
                disabled
                className="readonly-input"
              />
              <p className="profile-help-text">
                This matches your application and cannot be changed.
              </p>
            </div>

            <div className="input-group">
              <label htmlFor="display-name">Display Name</label>
              <input
                type="text"
                id="display-name"
                name="display_name"
                value={formData.display_name}
                onChange={handleInputChange}
                placeholder="Name displayed to students"
              />
            </div>

            <div className="input-group">
              <label htmlFor="headline">Headline</label>
              <input
                type="text"
                id="headline"
                name="headline"
                value={formData.headline}
                onChange={handleInputChange}
                placeholder="e.g. Certified English Teacher with 5 years exp."
              />
            </div>

            <div className="input-group">
              <label htmlFor="bio">Bio / Self Introduction</label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                rows={5}
                placeholder="Tell students about yourself..."
              />
            </div>

            <div className="input-group">
              <label htmlFor="years-of-experience">Years of Experience</label>
              <input
                type="number"
                id="years-of-experience"
                name="years_of_experience"
                value={formData.years_of_experience}
                onChange={handleInputChange}
                min="0"
              />
            </div>

            <div className="input-group">
              <label htmlFor="work-history">Work History</label>
              <p className="profile-help-text">
                Add your relevant teaching or professional experience.
              </p>
              <WorkHistoryEditor
                value={formData.work_history}
                onChange={(newHistory) => {
                  isDirtyRef.current = true;
                  setFormData(prev => ({ ...prev, work_history: newHistory }));
                }}
                onValidationChange={setIsWorkHistoryValid}
              />
            </div>
          </div>

          <div className="profile-panel-footer">
            <button id="update-button" className="update-btn" disabled={isUpdating} type="submit">
              {isUpdating ? <Loader /> : <span>Update Profile</span>}
            </button>
          </div>
        </form>
      </section>

      <ConfirmDialog
        isOpen={isRemoveConfirmOpen}
        title={t('profilePage.removeAvatarTitle', 'Remove Photo')}
        message={t('profilePage.confirmRemoveAvatar', 'Are you sure you want to remove your profile photo?')}
        onConfirm={executeRemoveAvatar}
        onCancel={() => setIsRemoveConfirmOpen(false)}
      />

      {isAvatarModalOpen && (
        <div
          className="profile-avatar-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => {
            if (!isAvatarUploading) closeAvatarModal();
          }}
        >
          <div className="profile-avatar-modal" onClick={(event) => event.stopPropagation()}>
            <div className="profile-avatar-modal-header">
              <div className="profile-avatar-modal-title">
                {t('profilePage.avatarCropTitle', 'Adjust your avatar')}
              </div>
              <button type="button" className="profile-avatar-modal-close" onClick={closeAvatarModal}>
                {t('profilePage.avatarCropClose', 'Close')}
              </button>
            </div>

            <div className="profile-avatar-modal-body">
              <div className="profile-cropper">
                <Cropper
                  image={avatarSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={handleCropComplete}
                  containerClassName="profile-cropper-container"
                  cropAreaClassName="profile-crop-area"
                  mediaClassName="profile-crop-media"
                />
              </div>

              <div className="profile-crop-controls">
                <label className="profile-crop-label" htmlFor="tutor-crop-zoom">
                  {t('profilePage.avatarZoom', 'Zoom')}
                </label>
                <input
                  id="tutor-crop-zoom"
                  className="profile-crop-range"
                  type="range"
                  min="1"
                  max="3"
                  step="0.05"
                  value={zoom}
                  disabled={isAvatarUploading}
                  onChange={(event) => setZoom(Number(event.target.value))}
                />
              </div>
            </div>

            <div className="profile-avatar-modal-footer">
              <button
                type="button"
                className="secondary-btn profile-avatar-modal-btn"
                onClick={closeAvatarModal}
                disabled={isAvatarUploading}
              >
                {t('profilePage.avatarCancel', 'Cancel')}
              </button>
              <button
                type="button"
                className="update-btn profile-avatar-modal-btn"
                onClick={handleAvatarSave}
                disabled={isAvatarUploading}
              >
                {isAvatarUploading ? t('profilePage.avatarSaving', 'Saving...') : t('profilePage.avatarSave', 'Use photo')}
              </button>
            </div>
          </div>
        </div>
      )}

      {isCoverModalOpen && (
        <div
          className="profile-cover-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={handleCoverOverlayClick}
        >
          <div className="profile-cover-modal" onClick={(event) => event.stopPropagation()}>
            <div className="profile-cover-modal-header">
              <div className="profile-cover-modal-title">
                {t('profilePage.coverCropTitle', 'Adjust your cover')}
              </div>
              <button
                type="button"
                className="profile-cover-modal-close"
                onClick={closeCoverModal}
                disabled={isCoverUploading}
              >
                {t('profilePage.coverCropClose', 'Close')}
              </button>
            </div>

            <div className="profile-cover-modal-body">
              <div className="profile-cover-preview">
                <div className="profile-cover-preview-frame">
                  <button
                    type="button"
                    className="profile-cover-resize-handle left"
                    aria-label={t('profilePage.coverResizeLeft', 'Resize cover preview left')}
                    onPointerDown={(event) => startCoverResize('resize-left', event)}
                    disabled={!hasCoverDraft}
                  />
                  <button
                    type="button"
                    className="profile-cover-resize-handle right"
                    aria-label={t('profilePage.coverResizeRight', 'Resize cover preview right')}
                    onPointerDown={(event) => startCoverResize('resize-right', event)}
                    disabled={!hasCoverDraft}
                  />
                  <div
                    className={`profile-cover-preview-canvas-wrap ${isCoverPanning ? 'is-grabbing' : ''}`}
                    onPointerDown={startCoverPan}
                    role="presentation"
                  >
                    <canvas
                      className="profile-cover-preview-canvas"
                      ref={coverPreviewCanvasRef}
                      width={coverPreviewWidth}
                      height={COVER_PREVIEW_HEIGHT}
                      aria-hidden="true"
                    />
                    <div className="profile-cover-drag-hint">
                      {t('profilePage.coverDragHint', 'Drag image to adjust position')}
                    </div>
                  </div>
                </div>
              </div>

              <div className="profile-cover-controls">
                <div className="profile-cover-helper">
                  {t('profilePage.coverPreviewWidth', 'Preview width')}: {coverPreviewWidth}px
                </div>
              </div>
            </div>

            <div className="profile-cover-modal-footer">
              <button
                type="button"
                className="secondary-btn profile-cover-modal-btn"
                onClick={closeCoverModal}
                disabled={isCoverUploading}
              >
                {t('profilePage.coverCancel', 'Cancel')}
              </button>
              <button
                type="button"
                className="update-btn profile-cover-modal-btn"
                onClick={handleCoverSave}
                disabled={!hasCoverDraft || isCoverUploading}
              >
                {isCoverUploading ? t('profilePage.coverSaving', 'Saving...') : t('profilePage.coverSave', 'Use cover')}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
